// src/Pages/AdminSubCategories.js
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../utils/axiosInstance";

export default function AdminSubCategories() {
  const navigate = useNavigate();

  // categories
  const [categories, setCategories] = useState([]);
  const [catLoading, setCatLoading] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState("");

  // subcategories list
  const [allSubCategories, setAllSubCategories] = useState([]);
  const [loading, setLoading] = useState(false);

  // search subcategories
  const [search, setSearch] = useState("");

  // drawer view
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);

  // add/edit modal
  const [modalOpen, setModalOpen] = useState(false);
  const [mode, setMode] = useState("ADD"); // ADD | EDIT
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(getEmptyForm());
  const [file, setFile] = useState(null);

  // delete
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteRow, setDeleteRow] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // preview
  const previewUrl = useMemo(() => (file ? URL.createObjectURL(file) : ""), [file]);
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  // auth + initial load
  useEffect(() => {
    const token = localStorage.getItem("token");
    const user = JSON.parse(localStorage.getItem("user") || "null");
    const role = (user?.role || "").toUpperCase();

    if (!token || !(role === "ADMIN" || role === "STAFF")) {
      navigate("/login", { replace: true });
      return;
    }

    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ================== API ================== */
  const fetchCategories = async () => {
    try {
      setCatLoading(true);
      const res = await axiosInstance.get("/api/categories");
      const list = Array.isArray(res.data) ? res.data : [];
      setCategories(list);

      // auto pick first category if exists
      if (list.length > 0) {
        const firstId = String(list[0].id);
        setSelectedCategoryId(firstId);
        fetchSubCategories(firstId);
      } else {
        setSelectedCategoryId("");
        setAllSubCategories([]);
      }
    } catch (err) {
      console.log("GET /api/categories error", err);
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        navigate("/login", { replace: true });
      } else {
        alert(err?.response?.data?.msg || "Failed to load categories");
      }
    } finally {
      setCatLoading(false);
    }
  };

  const fetchSubCategories = async (categoryId) => {
    try {
      setLoading(true);
      const cid = String(categoryId || "").trim();
      if (!cid) {
        setAllSubCategories([]);
        return;
      }

      const res = await axiosInstance.get("/api/subcategories", {
        params: { categoryId: cid },
      });

      const list = Array.isArray(res.data) ? res.data : [];
      setAllSubCategories(list);
    } catch (err) {
      console.log("GET /api/subcategories error", err);
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        navigate("/admin", { replace: true });
      } else {
        alert(err?.response?.data?.msg || "Failed to load subcategories");
      }
    } finally {
      setLoading(false);
    }
  };

  /* ================== HELPERS ================== */
  // ✅ same as your AdminProducts: base url from axiosInstance
  const imgUrl = (path) => {
    if (!path) return "";
    const base = (axiosInstance.defaults.baseURL || "").replace(/\/$/, "");
    if (path.startsWith("http")) return path;
    if (path.startsWith("/")) return `${base}${path}`;
    return `${base}/${path}`;
  };

  const selectedCategory = useMemo(() => {
    const cid = Number(selectedCategoryId);
    return categories.find((c) => Number(c.id) === cid) || null;
  }, [categories, selectedCategoryId]);

  const subcategories = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return allSubCategories;
    return allSubCategories.filter((s) => (s.name || "").toLowerCase().includes(q));
  }, [allSubCategories, search]);

  const total = subcategories.length;

  const onCategoryChange = (cid) => {
    setSelectedCategoryId(cid);
    setSearch("");
    fetchSubCategories(cid);
  };

  const openView = async (row) => {
    try {
      setDrawerOpen(true);
      setViewLoading(true);
      setSelected(null);

      // if backend has GET /api/subcategories/:id it will load full,
      // if not, fallback to row.
      const res = await axiosInstance.get(`/api/subcategories/${row.id}`);
      setSelected(res.data);
    } catch (err) {
      console.log("GET /api/subcategories/:id error (fallback to row)", err);
      setSelected(row);
    } finally {
      setViewLoading(false);
    }
  };

  const closeView = () => {
    setDrawerOpen(false);
    setSelected(null);
  };

  const openAdd = () => {
    if (!selectedCategoryId) return alert("Please create/select a category first");
    setMode("ADD");
    setForm({
      name: "",
      categoryId: selectedCategoryId,
    });
    setFile(null);
    setSelected(null);
    setModalOpen(true);
  };

  const openEdit = async (row) => {
    try {
      const res = await axiosInstance.get(`/api/subcategories/${row.id}`);
      const s = res.data || row;

      setMode("EDIT");
      setSelected(s);

      setForm({
        name: s?.name || "",
        categoryId: String(s?.categoryId ?? selectedCategoryId ?? ""),
      });

      setFile(null);
      setModalOpen(true);
    } catch (err) {
      console.log("openEdit error", err);
      setMode("EDIT");
      setSelected(row);

      setForm({
        name: row?.name || "",
        categoryId: String(row?.categoryId ?? selectedCategoryId ?? ""),
      });

      setFile(null);
      setModalOpen(true);
    }
  };

  const closeModal = () => {
    setModalOpen(false);
    setSaving(false);
    setFile(null);
  };

  const saveSubCategory = async () => {
    try {
      if (!form.categoryId) return alert("Category required");
      if (!form.name.trim()) return alert("Subcategory name required");

      // ADD must have image (you can change if your backend allows empty)
      if (mode === "ADD" && !file) return alert("Subcategory image required");

      setSaving(true);

      const fd = new FormData();
      fd.append("categoryId", String(form.categoryId));
      fd.append("name", form.name.trim());
      if (file) fd.append("image", file);

      if (mode === "ADD") {
        await axiosInstance.post("/api/subcategories", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        await axiosInstance.put(`/api/subcategories/${selected.id}`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }

      closeModal();

      // refresh list for current dropdown
      const cidToRefresh = String(selectedCategoryId || form.categoryId);
      if (cidToRefresh) fetchSubCategories(cidToRefresh);
    } catch (err) {
      console.log("saveSubCategory error", err);
      alert(err?.response?.data?.msg || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const openDelete = (row) => {
    setDeleteRow(row);
    setDeleteOpen(true);
  };

  const closeDelete = () => {
    setDeleteOpen(false);
    setDeleteRow(null);
  };

  const confirmDelete = async () => {
    if (!deleteRow?.id) return;
    try {
      setDeleteLoading(true);
      await axiosInstance.delete(`/api/subcategories/${deleteRow.id}`);
      closeDelete();
      fetchSubCategories(selectedCategoryId);
    } catch (err) {
      console.log("DELETE /api/subcategories/:id error", err);
      alert(err?.response?.data?.msg || "Delete failed");
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div style={styles.page}>
      {/* Topbar */}
      <div style={styles.topbar}>
        <h2 style={styles.title}>Sub Categories</h2>
        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={() => navigate("/admin/dashboard")} style={styles.btn}>
            ← Dashboard
          </button>
          <button onClick={openAdd} style={styles.primaryBtn} disabled={!selectedCategoryId}>
            + Add SubCategory
          </button>
        </div>
      </div>

      {/* Category Select + Search */}
      <div style={styles.filtersRow}>
        <div style={styles.selectWrap}>
          <div style={styles.selectLabel}>Category</div>
          <select
            style={styles.select}
            value={selectedCategoryId}
            onChange={(e) => onCategoryChange(e.target.value)}
            disabled={catLoading}
          >
            {categories.length === 0 ? (
              <option value="">No categories</option>
            ) : (
              categories.map((c) => (
                <option key={c.id} value={String(c.id)}>
                  {c.name}
                </option>
              ))
            )}
          </select>
        </div>

        <div style={styles.searchBar}>
          <div style={styles.searchIcon}>🔍</div>
          <input
            style={styles.searchInput}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search subcategory name..."
            disabled={!selectedCategoryId}
          />
          {search.trim() ? (
            <button type="button" style={styles.clearBtn} onClick={() => setSearch("")} title="Clear">
              ✕
            </button>
          ) : null}
        </div>
      </div>

      {/* Card + Table */}
      <div style={styles.card}>
        {!selectedCategoryId ? (
          <div style={styles.info}>
            No category selected. Please create categories first, then select.
          </div>
        ) : loading ? (
          <div style={styles.info}>Loading...</div>
        ) : (
          <>
            <div style={styles.meta}>
              Category: <b>{selectedCategory?.name || "-"}</b> • Total SubCategories: <b>{total}</b>
            </div>

            <div style={{ overflowX: "auto" }}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    {["ID", "SubCategory", "Image", "CategoryId", "Created", "Actions"].map((h) => (
                      <th key={h} style={styles.th}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {subcategories.length === 0 ? (
                    <tr>
                      <td colSpan={6} style={styles.empty}>
                        No subcategories found
                      </td>
                    </tr>
                  ) : (
                    subcategories.map((s) => (
                      <tr key={s.id}>
                        <td style={styles.td}>{s.id}</td>

                        <td style={styles.td}>
                          <div style={{ fontWeight: 800 }}>{s.name}</div>
                        </td>

                        <td style={styles.td}>
                          {s.image ? (
                            <img
                              src={imgUrl(s.image)}
                              alt={s.name}
                              style={styles.thumb}
                              onError={(e) => {
                                e.currentTarget.style.display = "none";
                              }}
                            />
                          ) : (
                            <div style={styles.thumbPlaceholder}>No Img</div>
                          )}
                        </td>

                        <td style={styles.td}>{s.categoryId ?? "-"}</td>

                        <td style={styles.td}>
                          {s.createdAt ? new Date(s.createdAt).toLocaleString() : "-"}
                        </td>

                        <td style={styles.td}>
                          <div style={styles.actions}>
                            <button style={styles.actionBtn} onClick={() => openView(s)}>
                              View
                            </button>
                            <button style={styles.actionBtn} onClick={() => openEdit(s)}>
                              Edit
                            </button>
                            <button style={styles.dangerBtn} onClick={() => openDelete(s)}>
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* DRAWER VIEW */}
      {drawerOpen && (
        <div style={styles.drawerOverlay} onClick={closeView}>
          <div style={styles.drawer} onClick={(e) => e.stopPropagation()}>
            <div style={styles.drawerHeader}>
              <h3 style={{ margin: 0 }}>SubCategory Details</h3>
              <button style={styles.btnSmall} onClick={closeView}>
                ✕
              </button>
            </div>

            {viewLoading ? (
              <div style={styles.info}>Loading...</div>
            ) : selected ? (
              <div style={styles.drawerBody}>
                <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 14 }}>
                  {selected.image ? (
                    <img
                      src={imgUrl(selected.image)}
                      alt="subcat"
                      style={{ width: 88, height: 88, borderRadius: 14, objectFit: "cover", border: "1px solid #eee" }}
                    />
                  ) : (
                    <div style={{ ...styles.thumbPlaceholder, width: 88, height: 88, borderRadius: 14 }}>No Img</div>
                  )}
                  <div>
                    <div style={{ fontSize: 18, fontWeight: 900 }}>{selected.name}</div>
                    <div style={{ fontSize: 12, color: "#666", marginTop: 4 }}>
                      ID: <b>{selected.id}</b> • CategoryId: <b>{selected.categoryId}</b>
                    </div>
                  </div>
                </div>

                <Row label="ID" value={selected.id} />
                <Row label="CategoryId" value={selected.categoryId} />
                <Row label="Name" value={selected.name} />
                <Row label="Image" value={selected.image || "-"} />
                <Row label="Created" value={selected.createdAt ? new Date(selected.createdAt).toLocaleString() : "-"} />
                <Row label="Updated" value={selected.updatedAt ? new Date(selected.updatedAt).toLocaleString() : "-"} />

                <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
                  <button style={styles.actionBtn} onClick={() => openEdit(selected)}>
                    Edit
                  </button>
                  <button style={styles.dangerBtn} onClick={() => openDelete(selected)}>
                    Delete
                  </button>
                </div>
              </div>
            ) : (
              <div style={styles.info}>No data</div>
            )}
          </div>
        </div>
      )}

      {/* ADD / EDIT MODAL */}
      {modalOpen && (
        <div style={styles.modalOverlay} onClick={closeModal}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={{ margin: 0 }}>{mode === "ADD" ? "Add SubCategory" : "Edit SubCategory"}</h3>
              <button style={styles.btnSmall} onClick={closeModal}>
                ✕
              </button>
            </div>

            <div style={styles.modalBody}>
              <div style={styles.grid}>
                <Field label="Category *">
                  <select
                    style={styles.modalInput}
                    value={form.categoryId}
                    onChange={(e) => setForm({ ...form, categoryId: e.target.value })}
                  >
                    {categories.map((c) => (
                      <option key={c.id} value={String(c.id)}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </Field>

                <Field label="SubCategory Name *">
                  <input
                    style={styles.modalInput}
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="vitamins"
                  />
                </Field>

                <div style={{ gridColumn: "1 / -1" }}>
                  <Field label={mode === "ADD" ? "Image *" : "Image (optional - upload to replace)"}>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const f = (e.target.files || [])[0] || null;
                        setFile(f);
                      }}
                    />

                    {/* new preview */}
                    {file && (
                      <div style={{ marginTop: 10 }}>
                        <div style={{ fontSize: 12, color: "#666", marginBottom: 6 }}>Preview</div>
                        <img src={previewUrl} alt="preview" style={styles.previewImgLarge} />
                      </div>
                    )}

                    {/* current image for edit */}
                    {mode === "EDIT" && !file && selected?.image && (
                      <div style={{ marginTop: 10 }}>
                        <div style={{ fontSize: 12, color: "#666", marginBottom: 6 }}>
                          Current Image (upload new to replace)
                        </div>
                        <img src={imgUrl(selected.image)} alt="current" style={styles.previewImgLarge} />
                      </div>
                    )}
                  </Field>
                </div>
              </div>
            </div>

            <div style={styles.modalFooter}>
              <button style={styles.btn} onClick={closeModal} disabled={saving}>
                Cancel
              </button>
              <button style={styles.primaryBtn} onClick={saveSubCategory} disabled={saving}>
                {saving ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DELETE CONFIRM */}
      {deleteOpen && (
        <div style={styles.modalOverlay} onClick={closeDelete}>
          <div style={styles.confirm} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>Delete SubCategory?</h3>
            <p style={{ marginTop: 6, fontSize: 14 }}>
              Are you sure you want to delete <b>{deleteRow?.name}</b>?
            </p>

            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button style={styles.btn} onClick={closeDelete} disabled={deleteLoading}>
                Cancel
              </button>
              <button style={styles.dangerBtn} onClick={confirmDelete} disabled={deleteLoading}>
                {deleteLoading ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* helpers */
function getEmptyForm() {
  return { name: "", categoryId: "" };
}

function Row({ label, value }) {
  return (
    <div style={styles.row}>
      <div style={styles.rowLabel}>{label}</div>
      <div style={styles.rowValue}>{value || "-"}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label style={styles.label}>{label}</label>
      {children}
    </div>
  );
}

/* styles */
const styles = {
  page: { padding: 22, background: "#f4f6f8", minHeight: "100vh" },
  topbar: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 },
  title: { margin: 0, fontSize: 34, fontWeight: 800, letterSpacing: -0.5 },

  btn: {
    padding: "12px 14px",
    cursor: "pointer",
    borderRadius: 10,
    border: "1px solid #cfcfcf",
    background: "#f3f3f3",
    fontSize: 14,
  },
  btnSmall: {
    padding: "8px 10px",
    cursor: "pointer",
    borderRadius: 10,
    border: "1px solid #cfcfcf",
    background: "#f3f3f3",
    fontSize: 13,
  },
  primaryBtn: {
    padding: "12px 16px",
    cursor: "pointer",
    background: "#111",
    color: "#fff",
    border: "none",
    borderRadius: 10,
    fontSize: 14,
  },

  filtersRow: { display: "flex", gap: 12, flexWrap: "wrap", alignItems: "stretch", marginBottom: 12 },

  selectWrap: {
    background: "#fff",
    border: "1px solid #e6e6e6",
    borderRadius: 16,
    padding: "10px 12px",
    boxShadow: "0 8px 22px rgba(0,0,0,0.06)",
    minWidth: 260,
  },
  selectLabel: { fontSize: 12, color: "#666", marginBottom: 6, fontWeight: 700 },
  select: { width: "100%", border: "1px solid #ddd", borderRadius: 12, padding: "10px 10px", outline: "none" },

  searchBar: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    background: "#fff",
    border: "1px solid #e6e6e6",
    borderRadius: 16,
    padding: "12px 14px",
    boxShadow: "0 8px 22px rgba(0,0,0,0.06)",
    maxWidth: 520,
    flex: 1,
    minWidth: 280,
  },
  searchIcon: { fontSize: 16, opacity: 0.75 },
  searchInput: { border: "none", outline: "none", fontSize: 15, flex: 1, background: "transparent" },
  clearBtn: {
    border: "none",
    background: "#f2f2f2",
    borderRadius: 10,
    cursor: "pointer",
    padding: "8px 10px",
    fontSize: 14,
  },

  card: { background: "#fff", borderRadius: 16, padding: 16, boxShadow: "0 10px 26px rgba(0,0,0,0.10)" },
  info: { padding: 10, fontSize: 14 },
  meta: { marginBottom: 10, color: "#222", fontSize: 16 },

  table: { width: "100%", borderCollapse: "collapse", minWidth: 980 },
  th: { textAlign: "left", padding: "12px 8px", borderBottom: "1px solid #eee", fontSize: 14, color: "#333", whiteSpace: "nowrap" },
  td: { padding: "14px 8px", borderBottom: "1px solid #f0f0f0", fontSize: 14, verticalAlign: "middle" },
  empty: { padding: 16, textAlign: "center", color: "#666", fontSize: 14 },

  thumb: { width: 56, height: 56, borderRadius: 14, objectFit: "cover", border: "1px solid #eee", background: "#fff" },
  thumbPlaceholder: {
    width: 56,
    height: 56,
    borderRadius: 14,
    border: "1px solid #eee",
    background: "#f3f3f3",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#888",
    fontSize: 11,
  },

  actions: { display: "flex", gap: 10, flexWrap: "wrap" },
  actionBtn: { padding: "9px 12px", cursor: "pointer", borderRadius: 10, border: "1px solid #ddd", background: "#fff", fontSize: 13 },
  dangerBtn: { padding: "9px 12px", cursor: "pointer", borderRadius: 10, border: "1px solid #ffcccc", background: "#ffecec", fontSize: 13 },

  // drawer
  drawerOverlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.25)", display: "flex", justifyContent: "flex-end", zIndex: 50 },
  drawer: { width: 420, maxWidth: "92vw", height: "100%", background: "#fff", padding: 14, boxShadow: "-10px 0 24px rgba(0,0,0,0.14)" },
  drawerHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", paddingBottom: 10, borderBottom: "1px solid #eee" },
  drawerBody: { paddingTop: 12 },

  row: { display: "grid", gridTemplateColumns: "150px 1fr", gap: 10, padding: "10px 0", borderBottom: "1px dashed #f0f0f0" },
  rowLabel: { color: "#666", fontSize: 12 },
  rowValue: { color: "#111", fontWeight: 700, fontSize: 13 },

  // modal
  modalOverlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.25)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 60, padding: 16 },
  modal: { width: 760, maxWidth: "96vw", background: "#fff", borderRadius: 16, boxShadow: "0 14px 32px rgba(0,0,0,0.18)", display: "flex", flexDirection: "column" },
  modalHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: 14, borderBottom: "1px solid #eee" },
  modalBody: { padding: 14, maxHeight: "70vh", overflowY: "auto" },
  modalFooter: { padding: 14, borderTop: "1px solid #eee", display: "flex", justifyContent: "flex-end", gap: 10, background: "#fff" },

  grid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 },
  label: { display: "block", fontSize: 12, color: "#666", marginBottom: 6 },
  modalInput: { width: "100%", padding: "10px 12px", borderRadius: 10, border: "1px solid #ddd", outline: "none", fontSize: 14 },

  previewImgLarge: { width: 160, height: 160, borderRadius: 16, objectFit: "cover", border: "1px solid #eee" },

  // confirm
  confirm: { width: 420, maxWidth: "95vw", background: "#fff", borderRadius: 14, padding: 16, boxShadow: "0 14px 32px rgba(0,0,0,0.18)" },
};
